import React, { useEffect, useState } from "react";
import { devLog } from "../../utils/devLog";
import { useTranslation } from "../../hooks/useTranslation";
import { useCheckout } from "../../context/CheckoutContext";
import { buildRedirectUrl, pickRedirectUrl } from "../../utils/redirectUrlBuilder";
import { useSkinModals } from "../../skins/SkinModalContext";
import { darkenColor } from "../../utils/commonUtils";

const TransactionStatusModal = ({ 
  show, 
  onClose, 
  // Back-compat: most integration views pass `setShow` instead of `onClose`
  setShow,
  status, 
  transactionId,
  hostedPageRedirect,
  fallbackRedirectUrl,
  // Back-compat: some integration views pass `redirectUrl` as the fallback redirect
  redirectUrl,
}) => {
  const { t } = useTranslation();
  const skinModals = useSkinModals();
  const isSuccess = status === true || status === "success";
  const [countdown, setCountdown] = useState(5);
  const { logoUrl, brandColor, merchantName } = useCheckout();
  const resolvedFallbackRedirect = pickRedirectUrl(fallbackRedirectUrl, redirectUrl);

  const closeModal = onClose || (typeof setShow === "function" ? () => setShow(false) : undefined);

  // CTA color: brand color on success, neutral red on failure.
  const ctaBackground = isSuccess ? brandColor || "#0f172a" : "#dc2626";
  const ctaHover = isSuccess
    ? brandColor
      ? darkenColor(brandColor, 12)
      : "#1e293b"
    : "#b91c1c";

  /**
   * Handles redirect with transaction parameters
   * Prioritizes hosted_page_redirect from backend, falls back to config redirect URL
   */
  const handleRedirect = () => {
    const finalRedirectUrl = buildRedirectUrl({
      primaryUrl: hostedPageRedirect,
      fallbackUrl: resolvedFallbackRedirect,
      transactionId,
      status: isSuccess ? 'success' : 'failed',
    });

    if (finalRedirectUrl) {
      devLog('Redirecting to:', finalRedirectUrl);
      window.location.href = finalRedirectUrl;
    } else {
      console.warn('No redirect URL available');
      closeModal?.();
    }
  };

  // When a skin owns the UI, suppress local countdown/auto-redirect — the skin
  // handles those (avoids two timers racing to redirect at the same time).
  const localCountdownActive = show && !skinModals;

  useEffect(() => {
    if (localCountdownActive) setCountdown(5);
  }, [localCountdownActive]);

  useEffect(() => {
    let timer;
    if (localCountdownActive && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (localCountdownActive && countdown === 0 && isSuccess) {
      handleRedirect();
    }
    return () => timer && clearTimeout(timer);
  }, [localCountdownActive, countdown]);

  // Push state to the skin's modal context (if any) and render nothing.
  // Only stable callbacks go in deps to avoid an infinite loop (see
  // PaymentProcessingModal for the same pattern + rationale).
  const openStatus  = skinModals?.openStatus;
  const closeStatus = skinModals?.closeStatus;
  useEffect(() => {
    if (!openStatus || !closeStatus) return;
    if (show) {
      openStatus({
        status,
        transactionId,
        hostedPageRedirect,
        fallbackRedirectUrl: resolvedFallbackRedirect,
        redirectUrl: resolvedFallbackRedirect,
        onClose: closeModal,
      });
    } else {
      closeStatus();
    }
  }, [openStatus, closeStatus, show, status, transactionId, hostedPageRedirect, resolvedFallbackRedirect]);

  if (skinModals) return null;
  if (!show) return null;

  const iconColor = isSuccess ? "#22c55e" : "#ef4444";

  return (
    <div className="altruon-status-overlay" role="dialog" aria-modal="true">
      <div className="altruon-status-card">
        {closeModal && (
          <button
            type="button"
            className="altruon-status-close"
            onClick={closeModal}
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Merchant logo */}
        <div
          className="altruon-status-logo"
          style={{ background: logoUrl ? "#fff" : brandColor || "#0f172a" }}
        >
          {logoUrl ? (
            <img src={logoUrl} alt={merchantName || "Merchant Logo"} />
          ) : (
            <span>{(merchantName || "M").substring(0, 1).toUpperCase()}</span>
          )}
        </div>

        {/* Animated SVG status icon */}
        <svg
          className="altruon-status-icon"
          viewBox="0 0 56 56"
          aria-hidden="true"
        >
          <circle
            cx="28"
            cy="28"
            r="26"
            fill="none"
            stroke={iconColor}
            strokeWidth="3"
            strokeLinecap="round"
            className="altruon-status-icon-circle"
          />
          {isSuccess ? (
            <polyline
              points="16,28 24,36 40,20"
              fill="none"
              stroke={iconColor}
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="altruon-status-icon-check"
            />
          ) : (
            <>
              <line
                x1="19" y1="19" x2="37" y2="37"
                stroke={iconColor}
                strokeWidth="3.5"
                strokeLinecap="round"
                className="altruon-status-icon-x1"
              />
              <line
                x1="37" y1="19" x2="19" y2="37"
                stroke={iconColor}
                strokeWidth="3.5"
                strokeLinecap="round"
                className="altruon-status-icon-x2"
              />
            </>
          )}
        </svg>

        <h2 className="altruon-status-title">
          {isSuccess
            ? t("modals.transactionStatus.success.title")
            : t("modals.transactionStatus.failed.title")}
        </h2>

        <p className="altruon-status-msg">
          {isSuccess
            ? t("modals.transactionStatus.success.message")
            : t("modals.transactionStatus.failed.message")}
        </p>

        <button
          type="button"
          className="altruon-status-cta"
          style={{ background: ctaBackground }}
          onMouseEnter={(e) => (e.currentTarget.style.background = ctaHover)}
          onMouseLeave={(e) => (e.currentTarget.style.background = ctaBackground)}
          onClick={handleRedirect}
        >
          {t("modals.transactionStatus.backButton")}
        </button>

        {isSuccess && (
          <p className="altruon-status-countdown">
            {t("modals.transactionStatus.autoRedirect", {
              variables: { count: countdown },
            })}
          </p>
        )}
      </div>

      <style jsx>{`
        .altruon-status-overlay {
          position: fixed;
          inset: 0;
          z-index: 1040;
          background: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          animation: aoFade 0.2s ease;
        }
        .altruon-status-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          box-shadow: 0 20px 50px -10px rgba(15, 23, 42, 0.25);
          max-width: 400px;
          width: 100%;
          padding: 32px 28px 24px;
          text-align: center;
          position: relative;
          font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter",
            "Segoe UI", Helvetica, Arial, sans-serif;
          animation: aoSlide 0.3s ease;
        }
        .altruon-status-close {
          position: absolute;
          top: 12px;
          right: 12px;
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 6px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s, color 0.15s;
        }
        .altruon-status-close:hover {
          background: #f1f5f9;
          color: #475569;
        }
        .altruon-status-logo {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          overflow: hidden;
          margin: 0 auto 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
        }
        .altruon-status-logo img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        .altruon-status-logo span {
          color: #fff;
          font-size: 18px;
          font-weight: 700;
        }
        .altruon-status-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 20px;
          display: block;
          animation: aoPop 0.45s ease 0.1s both;
        }
        .altruon-status-icon-circle {
          stroke-dasharray: 166;
          stroke-dashoffset: 166;
          animation: aoDrawCircle 0.55s ease 0.2s forwards;
        }
        .altruon-status-icon-check {
          stroke-dasharray: 48;
          stroke-dashoffset: 48;
          animation: aoDrawCheck 0.3s ease 0.7s forwards;
        }
        .altruon-status-icon-x1,
        .altruon-status-icon-x2 {
          stroke-dasharray: 30;
          stroke-dashoffset: 30;
        }
        .altruon-status-icon-x1 { animation: aoDrawX 0.28s ease 0.7s forwards; }
        .altruon-status-icon-x2 { animation: aoDrawX 0.28s ease 0.82s forwards; }
        .altruon-status-title {
          font-size: 22px;
          font-weight: 700;
          letter-spacing: -0.015em;
          color: #0f172a;
          margin: 0 0 8px;
        }
        .altruon-status-msg {
          font-size: 14.5px;
          color: #64748b;
          line-height: 1.55;
          margin: 0 0 24px;
        }
        .altruon-status-cta {
          width: 100%;
          padding: 12px 16px;
          color: #fff;
          font-size: 14.5px;
          font-weight: 600;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: background 0.15s, transform 0.1s;
          letter-spacing: 0.005em;
        }
        .altruon-status-cta:active {
          transform: translateY(1px);
        }
        .altruon-status-countdown {
          margin: 14px 0 0;
          font-size: 12.5px;
          color: #94a3b8;
        }
        @keyframes aoFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes aoSlide {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes aoPop {
          0%   { transform: scale(0.7); opacity: 0; }
          70%  { transform: scale(1.08); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes aoDrawCircle {
          to { stroke-dashoffset: 0; }
        }
        @keyframes aoDrawCheck {
          to { stroke-dashoffset: 0; }
        }
        @keyframes aoDrawX {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
};

export default TransactionStatusModal;